import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

type ErrorBoundaryProps = Readonly<{
  children: ReactNode;
}>;

type ErrorBoundaryState = Readonly<{
  hasError: boolean;
  message: string;
}>;

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = {
    hasError: false,
    message: 'An unexpected application error occurred.',
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      message: error.message || 'An unexpected application error occurred.',
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('BABS POS application error', error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 p-8 text-white">
        <section className="w-full max-w-xl rounded-2xl border border-slate-700 bg-slate-900 p-8 shadow-2xl">
          <div className="mb-6 flex items-center gap-3 text-amber-300">
            <AlertTriangle aria-hidden="true" size={28} />
            <h1 className="text-2xl font-semibold">BABS POS needs to restart</h1>
          </div>
          <p className="mb-6 text-slate-300">{this.state.message}</p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-3 font-medium text-slate-950 transition hover:bg-slate-100"
          >
            <RotateCcw aria-hidden="true" size={18} />
            Reload application
          </button>
        </section>
      </main>
    );
  }
}
